import {
  AfterViewChecked, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatCalendar, MatDatepickerModule} from '@angular/material/datepicker';
import {provideLuxonDateAdapter} from '@angular/material-luxon-adapter';
import {TippyDirective} from '@ngneat/helipopper';
import {EePipesModule} from '@ee/common/pipes';
import {User} from '@ee/common/models';
import {DateTime} from 'luxon';

/**
 * The combined **due-date + assignees** panel content — a `<mat-calendar>` plus a searchable row of assignee
 * avatars and Save/Cancel. Extracted as a lightweight standalone control so it can be reused by BOTH the
 * eviction `ee-workflow-task-info` overlay AND the PM case step (the `progress-indicator` imports it directly
 * without pulling in the heavy `TasksModule`). The mat-calendar's Luxon date adapter is provided globally
 * (`MatLuxonDateModule` at the app root), so no date-adapter provider is needed here.
 *
 * Domain-agnostic: emits `{dueDate, userIds}`; each host maps that to its own persistence. Selection is keyed
 * by user id (not list index) so the search filter can't desync it.
 */
@Component({
  selector: 'ee-step-scheduler-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatDatepickerModule, EePipesModule, TippyDirective],
  template: `
    <div class="step-scheduler ee__standard-shadow">
      <mat-calendar #calendar [selected]="selectedDate" [startAt]="startAt"
                    (selectedChange)="selectionChanged($event)"></mat-calendar>
      @if (allowNoDate) {
        <div class="date-clear-row">
          @if (selectedDate) {
            <button type="button" class="clear-link" (click)="clearDate()">Clear due date</button>
          } @else {
            <span class="no-date-hint">No due date</span>
          }
        </div>
      }
      <div class="mb-4">
        <div class="panel-header-row">
          <div class="panel-header">Assigned Users</div>
          @if (selectedIds.size) {
            <button type="button" class="clear-link" (click)="clearUsers()">Clear</button>
          }
        </div>
        <input type="text" class="assignee-search" [(ngModel)]="searchText" placeholder="Search people..." />
        @for (user of filteredUsers; track user.id) {
          <span class="small mr-1 user cursor-pointer" [tp]="user.display_name" tpPlacement="top" [tpDelay]="500"
            [tpAppendTo]="tooltipAppendTo" [tpZIndex]="10001"
            [attr.data-letters]="user.display_name | dataLetters" [ngStyle]="{'--user-selector-color': user.color}"
            [ngClass]="{'selected': selectedIds.has(user.id)}" (click)="toggleUser(user)"></span>
        }
      </div>
      <div class="flex flex-row justify-between">
        <button mat-stroked-button type="button" (click)="onCancel.emit()">Cancel</button>
        <button mat-flat-button color="accent" type="button" (click)="save()">Save</button>
      </div>
    </div>
  `,
  styleUrls: ['./step-scheduler-panel.component.scss'],
  // Pin the mat-calendar to the Luxon date adapter at the component level, so it works regardless of whatever
  // DateAdapter an ancestor injector happens to provide (some feature subtrees provide the native adapter).
  providers: [provideLuxonDateAdapter()],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StepSchedulerPanelComponent implements OnInit, AfterViewChecked {
  /** Pre-selected due date (ISO-8601); null/absent defaults the calendar to today. */
  @Input() dueDate?: string | null;
  /** Pre-selected assignee ids. */
  @Input() assignedUserIds?: string[];
  /** The candidate users rendered as avatars. */
  @Input() users: User[] = [];
  /** When true, the due date is optional: a "Clear due date" affordance is shown and no date defaults to today. */
  @Input() allowNoDate = false;

  @Output() onSave = new EventEmitter<{ dueDate: string | null; userIds: string[] }>();
  @Output() onCancel = new EventEmitter<void>();

  selectedDate: DateTime | null = null;
  /** The month the calendar opens on (the selected date, else today). */
  startAt: DateTime = DateTime.local();
  selectedIds = new Set<string>();
  searchText = '';
  private loading = true;
  /** Append the avatar tooltips to this panel's own host element — it sits INSIDE the CDK overlay, so the
   *  tooltip is in the overlay's stacking context and renders on top of the popover (no z-index fight), while
   *  being a sibling of the `overflow:auto` card so it isn't clipped. */
  readonly tooltipAppendTo = () => this.elementRef.nativeElement;

  constructor(private readonly elementRef: ElementRef) {}

  @ViewChild(MatCalendar) calendar: MatCalendar<DateTime>;

  ngOnInit(): void {
    this.selectedDate = this.dueDate
      ? DateTime.fromISO(this.dueDate)
      : (this.allowNoDate ? null : DateTime.local());
    this.startAt = this.selectedDate ?? DateTime.local();
    (this.assignedUserIds ?? []).forEach(id => this.selectedIds.add(id));
  }

  ngAfterViewChecked(): void {
    if (this.loading && this.calendar) {
      this.calendar.activeDate = this.selectedDate ?? DateTime.local();
      this.calendar.updateTodaysDate();
      this.loading = false;
    }
  }

  /** Clear the due date (only offered when `allowNoDate`); the step keeps its assignees but no deadline. */
  clearDate(): void {
    this.selectedDate = null;
  }

  /** Deselect all assignees. */
  clearUsers(): void {
    this.selectedIds.clear();
  }

  /** Users matching the search box (all when blank), filtered by display name. */
  get filteredUsers(): User[] {
    const all = this.users ?? [];
    const q = this.searchText.trim().toLowerCase();
    return q ? all.filter(u => (u.display_name ?? '').toLowerCase().includes(q)) : all;
  }

  selectionChanged(event: DateTime): void {
    this.selectedDate = event;
    this.calendar?.updateTodaysDate();
  }

  toggleUser(user: User): void {
    if (!user.id) {
      return;
    }
    if (this.selectedIds.has(user.id)) {
      this.selectedIds.delete(user.id);
    } else {
      this.selectedIds.add(user.id);
    }
  }

  save(): void {
    const userIds = (this.users ?? []).filter(u => !!u.id && this.selectedIds.has(u.id)).map(u => u.id);
    this.onSave.emit({dueDate: this.toIso(this.selectedDate), userIds});
  }

  /** Coerce the calendar value to an ISO string, tolerating either a Luxon DateTime or a native Date. */
  private toIso(d: unknown): string | null {
    if (!d) {
      return null;
    }
    if (typeof (d as DateTime).toISO === 'function') {
      return (d as DateTime).toISO();
    }
    if (d instanceof Date) {
      return DateTime.fromJSDate(d).toISO();
    }
    return null;
  }
}
